<template>
  <div class="parts-panel" @click.stop>
    <div class="parts-header">
      <span class="parts-title">部件管理</span>
      <button class="close-btn" @click="$emit('close')"><X :size="14" /></button>
    </div>

    <div v-if="parts.length === 0" class="empty-parts">
      <p>未检测到子部件</p>
    </div>

    <div v-else class="parts-list">
      <div
        v-for="part in parts"
        :key="part.name"
        class="part-row"
      >
        <button
          :class="['vis-btn', { hidden: !part.visible }]"
          @click="togglePart(part)"
          :title="part.visible ? '点击隐藏' : '点击显示'"
        >
          <Eye v-if="part.visible" :size="13" />
          <EyeOff v-else :size="13" />
        </button>
        <span class="part-name" :class="{ hidden: !part.visible }">{{ part.name || '(无名部件)' }}</span>
        <button class="mat-btn" @click="editPartMaterial(part)" title="编辑材质">
          <Palette :size="12" />
        </button>
      </div>
    </div>

    <div class="parts-footer">
      <button class="btn-sm" @click="showAll">全部显示</button>
      <button class="btn-sm" @click="hideAll">全部隐藏</button>
    </div>

    <!-- 子部件材质编辑浮层 -->
    <MaterialEditPanel
      v-if="editingPart"
      :object-id="objectId"
      :mesh-name="editingPart"
      :on-history-push="props.onHistoryPush"
      class="part-mat-panel"
      @close="editingPart = null"
      @changed="onMaterialChanged"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import * as THREE from 'three'
import { X, Eye, EyeOff, Palette } from 'lucide-vue-next'
import { getThreeObject } from '../three/ObjectManager'
import { useSceneStore } from '../stores/scene'
import type { MaterialOverride } from '../types/scene'
import MaterialEditPanel from './MaterialEditPanel.vue'

const props = defineProps<{ objectId: string; onHistoryPush?: () => void }>()
const emit = defineEmits<{ close: [] }>()

const sceneStore = useSceneStore()
const editingPart = ref<string | null>(null)

interface PartInfo {
  name: string
  visible: boolean
}

const parts = ref<PartInfo[]>([])

function buildPartsList() {
  const group = getThreeObject(props.objectId)
  if (!group) return

  const obj = sceneStore.activeObjects().find(o => o.id === props.objectId)
  const hiddenMeshes = obj?.hiddenMeshes ?? []

  const names = new Set<string>()
  group.traverse(child => {
    if ((child instanceof THREE.Mesh || child instanceof THREE.Group) && child !== group) {
      if (child.name) names.add(child.name)
    }
  })

  parts.value = [...names].map(name => ({
    name,
    visible: !hiddenMeshes.includes(name)
  }))
}

watch(() => props.objectId, buildPartsList, { immediate: true })

function togglePart(part: PartInfo) {
  part.visible = !part.visible
  syncHiddenMeshes()

  // 实时更新 Three.js 对象可见性
  const group = getThreeObject(props.objectId)
  if (group) {
    group.traverse(child => {
      if (child.name === part.name) child.visible = part.visible
    })
  }
}

function syncHiddenMeshes() {
  const hidden = parts.value.filter(p => !p.visible).map(p => p.name)
  props.onHistoryPush?.()
  sceneStore.updateObject(props.objectId, { hiddenMeshes: hidden })
}

function showAll() {
  parts.value.forEach(p => p.visible = true)
  syncHiddenMeshes()
  const group = getThreeObject(props.objectId)
  if (group) group.traverse(child => { child.visible = true })
}

function hideAll() {
  parts.value.forEach(p => p.visible = false)
  syncHiddenMeshes()
  const group = getThreeObject(props.objectId)
  if (group) {
    group.traverse(child => {
      if (child !== group) child.visible = false
    })
  }
}

function editPartMaterial(part: PartInfo) {
  editingPart.value = editingPart.value === part.name ? null : part.name
}

function onMaterialChanged(_overrides: MaterialOverride[]) {
  // 材质变化已通过 MaterialEditPanel 写入 store
}
</script>

<style scoped>
.parts-panel {
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 10px;
  width: 220px;
  padding: 0;
  user-select: none;
  box-shadow: 0 8px 32px rgba(0,0,0,0.4);
  position: relative;
}

.parts-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px 8px;
  border-bottom: 1px solid #334155;
}

.parts-title { font-size: 13px; font-weight: 600; color: #e2e8f0; }

.close-btn {
  background: none; border: none; cursor: pointer;
  color: #64748b; padding: 2px; border-radius: 3px; display: flex; align-items: center;
}
.close-btn:hover { color: #e2e8f0; background: #334155; }

.empty-parts {
  padding: 20px;
  text-align: center;
  font-size: 12px;
  color: #475569;
}

.parts-list {
  max-height: 240px;
  overflow-y: auto;
  padding: 4px 0;
}

.part-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
}

.part-row:hover { background: #1a2a3a; }

.vis-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #64748b;
  padding: 3px;
  border-radius: 3px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.vis-btn:hover { color: #60a5fa; }
.vis-btn.hidden { color: #334155; }

.part-name {
  flex: 1;
  font-size: 12px;
  color: #cbd5e1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.part-name.hidden { color: #475569; text-decoration: line-through; }

.mat-btn {
  background: none;
  border: 1px solid #334155;
  cursor: pointer;
  color: #64748b;
  padding: 3px 5px;
  border-radius: 3px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.mat-btn:hover { border-color: #3b82f6; color: #60a5fa; }

.parts-footer {
  display: flex;
  gap: 6px;
  padding: 8px 10px;
  border-top: 1px solid #334155;
}

.btn-sm {
  flex: 1;
  font-size: 11px;
  padding: 5px 0;
  border-radius: 5px;
  cursor: pointer;
  background: #0f172a;
  border: 1px solid #334155;
  color: #64748b;
}
.btn-sm:hover { border-color: #475569; color: #94a3b8; }

.part-mat-panel {
  position: absolute;
  left: calc(100% + 8px);
  top: 0;
  z-index: 100;
}
</style>
